import {useEffect} from 'react';
import {Form} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {useNavigate, useParams} from 'react-router-dom';
import {
  CREATE_QUICK_ACTION_CATEGORY,
  UPDATE_QUICK_ACTION_CATEGORY,
  GET_QUICK_ACTION_CATEGORY_BY_ID,
} from '../resolvers';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';

export const useQuickActionCategoryForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const {id} = useParams();
  const isEditMode = !!id;

  // Watch form fields
  const name = Form.useWatch('name', form) || '';

  const [createCategory, {loading: creating}] = useMutation(
    CREATE_QUICK_ACTION_CATEGORY,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [updateCategory, {loading: updating}] = useMutation(
    UPDATE_QUICK_ACTION_CATEGORY,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);

  const {data, loading: loadingCategory} = useQuery(
    GET_QUICK_ACTION_CATEGORY_BY_ID,
    {
      variables: {categoryId: id},
      skip: !isEditMode,
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (
      isEditMode &&
      data?.getQuickActionCategoryById?.__typename ===
        'ResponseWithQuickActionCategory'
    ) {
      const category = data?.getQuickActionCategoryById?.data;
      if (category) {
        const translations = category.translations || [];
        const frTranslation = translations.find(t => t.language === 'FR');

        form.setFieldsValue({
          name: category.name || '',
          nameFR: frTranslation?.name || '',
          isActive: category.isActive ?? true,
        });
      }
    }
  }, [data, isEditMode, form]);

  const handleTranslation = () => {
    if (!name) return;

    translateText({
      variables: {
        sourceLang: 'EN',
        targetLang: 'FR',
        sourceText: name,
      },
    })
      .then(({data: {translateText}}) => {
        if (
          translateText.statusCode === 400 ||
          translateText.__typename === 'Error'
        ) {
          toast.error('Error Translating Text', translateText.message);
        } else {
          toast.success('Translated Successfully');
          form.setFieldValue('nameFR', translateText?.targetText);
        }
      })
      .catch(err => toast.error(err.message || 'An error occurred'));
  };

  const handleSubmit = values => {
    const input = {
      name: values.name,
      language: 'EN',
      isActive: values.isActive ?? true,
      ...(values.nameFR && {
        translations: [
          {
            name: values.nameFR,
            language: 'FR',
          },
        ],
      }),
    };

    if (isEditMode) {
      updateCategory({
        variables: {
          categoryId: id,
          input,
        },
      })
        .then(({data: {updateQuickActionCategory}}) => {
          if (updateQuickActionCategory.__typename === 'Error') {
            toast.error(updateQuickActionCategory.message);
          } else {
            toast.success(updateQuickActionCategory.message);
            navigate('/dashboard/quick-actions/categories');
          }
        })
        .catch(error => toast.error(error.message));
    } else {
      createCategory({
        variables: {
          input,
        },
      })
        .then(({data: {createQuickActionCategory}}) => {
          if (createQuickActionCategory.__typename === 'Error') {
            toast.error(createQuickActionCategory.message);
          } else {
            toast.success(createQuickActionCategory.message);
            navigate('/dashboard/quick-actions/categories');
          }
        })
        .catch(error => toast.error(error.message));
    }
  };

  // Get category name from query data or form (for dynamic updates)
  const categoryNameFromData =
    isEditMode &&
    data?.getQuickActionCategoryById?.__typename ===
      'ResponseWithQuickActionCategory'
      ? data?.getQuickActionCategoryById?.data?.name
      : null;
  const categoryNameFromForm = Form.useWatch('name', form) || '';
  const categoryName = isEditMode
    ? categoryNameFromForm || categoryNameFromData
    : null;

  return {
    form,
    name,
    handleTranslation,
    handleSubmit,
    loading: creating || updating,
    loadingCategory,
    loadingTranslation,
    isEditMode,
    categoryName,
  };
};
